import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api, money, formatApiErrorDetail, qtyLabel, perUomLabel, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { FileText, Check, X, Sparkles, Upload } from "lucide-react";
import SupplierDocumentTab from "@/pages/SupplierDocumentTab";

const STATUSES = ["Draft", "Under Review", "Sourcing", "Quoting", "Comparison", "Awarded", "Delivered", "Closed"];

const MAX_QUOTE_FILES = 5;
const MAX_MB = 25;
const ALLOWED_EXTS = ["pdf", "docx", "xlsx", "png", "jpg", "jpeg"];
const humanSize = (b) => (b == null ? "" : (b / 1024 < 1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(2)} MB`));

// Decimal-safe compute using JS Numbers with careful rounding at output.
const toNum = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
const round2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;

export default function RFQDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { can, loaded: permsLoaded } = usePermissions();
  const [rfq, setRfq] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [invSelect, setInvSelect] = useState({});
  const [supQuote, setSupQuote] = useState({ unit_price: "", currency: "CAD", lead_time_days: "", warranty: "", shipping_terms: "", shipping_cost: 0, other_costs: 0, notes: "" });
  const [supDocs, setSupDocs] = useState([]);
  const [supDocIds, setSupDocIds] = useState([]);
  const [openBQ, setOpenBQ] = useState(null);
  const [bqForm, setBqForm] = useState({ markup_pct: 12, lead_time_days: "", warranty: "", shipping_terms: "", notes: "" });
  // Supplier-facing attachment package (admin/AM only) — separate from
  // buyer's private document_ids. Kept as an in-memory draft until the
  // admin explicitly clicks Save draft / Approve.
  const [pkgSelected, setPkgSelected] = useState([]);
  const [pkgSaving, setPkgSaving] = useState(false);
  const [pkgUploading, setPkgUploading] = useState(false);
  const [pkgExtraDocs, setPkgExtraDocs] = useState({}); // id -> filename for AM-uploaded files not yet reloaded

  const load = async () => {
    const { data } = await api.get(`/rfqs/${id}`);
    setRfq(data);
    if (user.role === "supplier" && data.my_quote) {
      setSupQuote({
        unit_price: data.my_quote.unit_price ?? data.my_quote.base_price ?? "",
        currency: data.my_quote.currency ?? "CAD",
        lead_time_days: data.my_quote.lead_time_days ?? "",
        warranty: data.my_quote.warranty ?? "",
        shipping_terms: data.my_quote.shipping_terms ?? "",
        shipping_cost: data.my_quote.shipping_cost ?? 0,
        other_costs: data.my_quote.other_costs ?? 0,
        notes: data.my_quote.notes ?? "",
      });
      const existingDocs = data.my_quote.documents || [];
      setSupDocs(existingDocs.map((d) => ({ id: d.id, filename: d.original_filename, size: d.size })));
      setSupDocIds(existingDocs.map((d) => d.id));
    }
    if (user.role === "admin" || user.role === "account_manager") {
      // Hydrate the supplier-package draft with whatever is already saved
      // server-side; the admin can then check/uncheck to change the include list.
      const pkgDocs = data.supplier_package?.documents || [];
      setPkgSelected(pkgDocs.map((d) => d.id));
    }
  };

  useEffect(() => { load(); }, [id]);

  // Phase 3: only fetch the supplier directory for the invite step when the
  // employee actually holds `invitation.send`. Support/Finance/Doc-Reviewer
  // do not — we skip the fetch to avoid a 403 on their behalf.
  useEffect(() => {
    if (!permsLoaded) return;
    if ((user.role === "admin" || user.role === "account_manager") && rfq && can("invitation.send")) {
      (async () => {
        try {
          const { data } = await api.get(`/suppliers${rfq.category ? `?category=${encodeURIComponent(rfq.category)}` : ""}`);
          setSuppliers(data);
        } catch { /* backend is authoritative — 403 simply means no directory */ }
      })();
    }
  }, [rfq?.category, user.role, permsLoaded, can]);

  // Live supplier subtotal preview
  const supPreview = useMemo(() => {
    if (!rfq) return null;
    const up = toNum(supQuote.unit_price);
    const qty = toNum(rfq.quantity);
    const ship = toNum(supQuote.shipping_cost);
    const other = toNum(supQuote.other_costs);
    const sub = round2(up * qty);
    const total = round2(sub + ship + other);
    return { unit_price: up, subtotal: sub, shipping: round2(ship), other: round2(other), total };
  }, [supQuote, rfq]);

  // Live admin buyer-quote preview (must be declared before any early return to preserve hook order)
  const bqPreview = useMemo(() => {
    if (!openBQ || !rfq) return null;
    const supUnit = toNum(openBQ.unit_price ?? openBQ.base_price ?? 0);
    const qty = toNum(rfq.quantity);
    const ship = toNum(openBQ.shipping_cost ?? 0);
    const other = toNum(openBQ.other_costs ?? 0);
    const markup = toNum(bqForm.markup_pct);
    const buyerUnit = round2(supUnit * (1 + markup / 100));
    const supMerch = round2(supUnit * qty);
    const buyerMerch = round2(buyerUnit * qty);
    const supTotal = round2(supMerch + ship + other);
    const buyerTotal = round2(buyerMerch + ship + other);
    const margin = round2(buyerTotal - supTotal);
    return { supUnit, qty, ship, other, markup, buyerUnit, supMerch, buyerMerch, supTotal, buyerTotal, margin };
  }, [openBQ, bqForm, rfq]);

  if (!rfq) return <div className="text-slate-500">Loading…</div>;

  const uom = rfq.uom || rfq.unit || "Each";
  const per = perUomLabel(uom);

  const changeStatus = async (status) => {
    try { await api.patch(`/rfqs/${id}/status`, { status }); toast.success(`Moved to ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const invite = async () => {
    const ids = Object.keys(invSelect).filter((k) => invSelect[k]);
    if (!ids.length) return;
    try {
      await api.post(`/rfqs/${id}/invitations`, { supplier_ids: ids });
      toast.success(`Invited ${ids.length} supplier(s)`);
      setInvSelect({}); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---- Supplier attachment package (admin/AM only) ----
  const togglePkg = (docId) =>
    setPkgSelected((prev) => (prev.includes(docId) ? prev.filter((x) => x !== docId) : [...prev, docId]));

  const savePackage = async () => {
    setPkgSaving(true);
    try {
      await api.put(`/rfqs/${id}/supplier-package`, { document_ids: pkgSelected });
      toast.success("Package saved as draft — approve to enable invites");
      setPkgExtraDocs({});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setPkgSaving(false); }
  };

  const approvePackage = async () => {
    // Always PUT-then-approve so the approval covers the visible selection —
    // avoids stale approvals if the admin edited without saving.
    setPkgSaving(true);
    try {
      await api.put(`/rfqs/${id}/supplier-package`, { document_ids: pkgSelected });
      await api.post(`/rfqs/${id}/supplier-package/approve`);
      toast.success("Supplier package approved — you can now send invitations");
      setPkgExtraDocs({});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setPkgSaving(false); }
  };

  const uploadPackageFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (pkgSelected.length >= MAX_QUOTE_FILES) { toast.error(`Up to ${MAX_QUOTE_FILES} files per package.`); break; }
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) { toast.error(`${f.name}: unsupported type.`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name}: exceeds ${MAX_MB} MB`); continue; }
      setPkgUploading(true);
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/files/upload", fd);
        setPkgSelected((prev) => [...prev, data.id]);
        setPkgExtraDocs((prev) => ({ ...prev, [data.id]: f.name }));
        toast.success(`Uploaded ${f.name} — remember to Save draft or Approve`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      } finally { setPkgUploading(false); }
    }
    e.target.value = "";
  };

  const submitSupplierQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rfqs/${id}/supplier-quotes`, {
        unit_price: toNum(supQuote.unit_price),
        currency: supQuote.currency,
        lead_time_days: toNum(supQuote.lead_time_days),
        warranty: supQuote.warranty,
        shipping_terms: supQuote.shipping_terms,
        shipping_cost: toNum(supQuote.shipping_cost),
        other_costs: toNum(supQuote.other_costs),
        notes: supQuote.notes,
        document_ids: supDocIds,
      });
      toast.success("Quote submitted"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadSupDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (supDocs.length >= MAX_QUOTE_FILES) { toast.error(`Up to ${MAX_QUOTE_FILES} attachments per quote.`); break; }
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) { toast.error(`${f.name}: unsupported type.`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name}: exceeds ${MAX_MB} MB`); continue; }
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setSupDocs((d) => [...d, data]);
        setSupDocIds((s) => [...s, data.id]);
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    }
    e.target.value = "";
  };
  const removeSupDoc = (fid) => {
    setSupDocs((d) => d.filter((x) => x.id !== fid));
    setSupDocIds((s) => s.filter((x) => x !== fid));
  };

  const submitBuyerQuote = async () => {
    try {
      await api.post(`/rfqs/${id}/buyer-quotes`, {
        supplier_quote_id: openBQ.id,
        markup_pct: toNum(bqForm.markup_pct),
        lead_time_days: toNum(bqForm.lead_time_days || openBQ.lead_time_days),
        warranty: bqForm.warranty || openBQ.warranty,
        shipping_terms: bqForm.shipping_terms || openBQ.shipping_terms,
        notes: bqForm.notes,
      });
      toast.success("Buyer-facing quote prepared");
      setOpenBQ(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const selectBQ = async (bqId) => {
    try { await api.post(`/rfqs/${id}/buyer-quotes/${bqId}/select`); toast.success("Quote awarded — transaction created"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const declineBQ = async (bqId) => {
    try { await api.post(`/rfqs/${id}/buyer-quotes/${bqId}/decline`); toast.success("Quote declined"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const isEmployee = user.role === "admin" || user.role === "account_manager";
  // Deny-by-default until permissions are resolved so employees never see
  // a privileged control briefly during the perms fetch.
  const permReady = !isEmployee || permsLoaded;
  const canChangeStatus = permReady && can("rfq.status.write");
  const canCuratePkg = permReady && can("package.curate");
  const canApprovePkg = permReady && can("package.approve");
  const canInvite = permReady && can("invitation.send");
  const canBuildBQ = permReady && can("buyer_quote.write");
  const canSetMarkup = permReady && can("commercial.markup.write");
  const canReadSupplierCost = permReady && can("commercial.supplier_cost.read");
  const canReadMargin = permReady && can("commercial.margin.read");
  const canReadSupplierQuotes = permReady && can("supplier_quote.read");
  const hasAnyEmployeeTab = isEmployee && (canReadSupplierQuotes || canInvite || canBuildBQ || can("spec.extract") || can("spec.review") || can("spec.generate") || canCuratePkg);
  // Legacy alias kept for existing markup only used to broaden a few checks.
  const isAdmin = isEmployee;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span className="eyebrow" data-testid="rfq-reference">{rfq.reference}</span>
            <StatusBadge status={rfq.status} />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-[#0A2540] tracking-tight mt-2" data-testid="rfq-title">{rfq.title}</h1>
          <div className="text-sm text-slate-500 mt-1">{rfq.category} · {rfq.buyer?.company_name}</div>
        </div>
        {canChangeStatus && (
          <div className="flex items-center gap-2">
            <Select value={rfq.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-44" data-testid="rfq-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 gap-4 ${user.role === "supplier" ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
        {[
          ["Quantity", <span data-testid="rfq-qty-label">{qtyLabel(rfq.quantity, uom)}</span>],
          ["Delivery", rfq.delivery_location],
          ["Deadline", rfq.delivery_deadline],
          // Phase 3: target budget is server-side redacted for staff without
          // `buyer.private.read` — presence of the field is the source of truth.
          ...(user.role === "supplier" ? [] : [["Target budget", ("target_budget" in rfq && rfq.target_budget) ? money(rfq.target_budget) : "—"]]),
        ].map(([k, v]) => (
          <Card key={k} className="p-4 border-slate-200" data-testid={`rfq-fact-${String(k).toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="eyebrow text-slate-400">{k}</div>
            <div className="font-medium text-[#0A2540] mt-1">{v}</div>
          </Card>
        ))}
      </div>

      <Card className="p-6 border-slate-200">
        <div className="eyebrow mb-2">Specifications</div>
        <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{rfq.description}</div>
        {rfq.notes && <div className="mt-4"><div className="eyebrow mb-1">Buyer notes</div><div className="text-sm text-slate-600">{rfq.notes}</div></div>}
        {rfq.documents?.length > 0 && (
          <div className="mt-5">
            <div className="eyebrow mb-2">
              {(user.role === "admin" || user.role === "account_manager")
                ? "Buyer attachments (private submission record)"
                : "Attachments"}
            </div>
            {(user.role === "admin" || user.role === "account_manager") && (
              <div className="text-xs text-slate-500 mb-2">
                These files are only visible to the buyer and Caprocure staff. Suppliers will only receive files you explicitly include in the supplier package below.
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {rfq.documents.map((d) => (
                <a key={d.id} href="#" onClick={(e) => { e.preventDefault(); downloadFile(d.id, d.original_filename).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cannot open file")); }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 text-sm hover:bg-slate-50" data-testid={`doc-${d.id}`}>
                  <FileText className="w-4 h-4" /> {d.original_filename}
                </a>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Supplier attachment package — permission-gated curation UI */}
      {canCuratePkg && rfq.supplier_package && (
        <Card className="p-6 border-amber-200 bg-amber-50/40" data-testid="supplier-package-card">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="eyebrow">Supplier attachment package</div>
              <h3 className="font-display font-bold text-[#0A2540]">Curate what suppliers will see</h3>
            </div>
            <span
              className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${rfq.supplier_package.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
              data-testid="pkg-status">
              {rfq.supplier_package.status}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 mb-4">
            Buyer uploads stay private. Tick each file you want to include in the supplier-facing package, or upload a replacement / additional specification below. Any change re-drafts the package — you must Approve again before invitations can be sent.
          </p>

          {rfq.documents?.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-slate-600 mb-2">Include from the buyer's uploads:</div>
              <div className="space-y-1">
                {rfq.documents.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-white cursor-pointer" data-testid={`pkg-buyer-doc-${d.id}`}>
                    <input type="checkbox" checked={pkgSelected.includes(d.id)} onChange={() => togglePkg(d.id)} data-testid={`pkg-cb-${d.id}`} />
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span>{d.original_filename}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <div className="text-xs font-semibold text-slate-600 mb-2">Or upload a replacement / additional specification:</div>
            <Input type="file" onChange={uploadPackageFile} disabled={pkgUploading || pkgSelected.length >= MAX_QUOTE_FILES} accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg" data-testid="pkg-upload-input" />
          </div>

          <div className="mb-4 p-3 rounded border border-slate-200 bg-white" data-testid="pkg-preview">
            <div className="text-xs font-semibold text-slate-600 mb-2">Suppliers will receive ({pkgSelected.length}):</div>
            {pkgSelected.length === 0 ? (
              <div className="text-xs text-slate-500">No files — suppliers will see no attachments.</div>
            ) : (
              <ul className="text-sm space-y-1">
                {pkgSelected.map((fid) => {
                  const doc = [...(rfq.documents || []), ...(rfq.supplier_package.documents || [])].find((d) => d.id === fid);
                  const label = doc?.original_filename || pkgExtraDocs[fid] || fid;
                  return (
                    <li key={fid} className="flex items-center gap-2" data-testid={`pkg-preview-${fid}`}>
                      <FileText className="w-4 h-4 text-slate-500" />{label}
                      <button onClick={() => togglePkg(fid)} className="ml-auto text-xs text-rose-600 hover:text-rose-700" data-testid={`pkg-remove-${fid}`}>remove</button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={savePackage} disabled={pkgSaving || pkgUploading} data-testid="pkg-save-btn">Save draft</Button>
            {canApprovePkg && (
              <Button onClick={approvePackage} disabled={pkgSaving || pkgUploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pkg-approve-btn">
                {rfq.supplier_package.status === "approved" ? "Re-approve" : "Approve package"}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Buyer comparison */}
      {user.role === "buyer" && (rfq.buyer_quotes?.length > 0) && (
        <Card className="p-6 border-slate-200">
          <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-[#C8102E]" /> <div className="eyebrow">Prepared for you</div></div>
          <h2 className="font-display text-xl font-bold text-[#0A2540]">Quote comparison</h2>
          <div className="text-xs text-slate-500 mb-5">Compare options below. Total Landed Price is what you pay for the complete order.</div>
          <div className="grid md:grid-cols-2 gap-4">
            {rfq.buyer_quotes.map((bq) => (
              <div key={bq.id} className={`rounded-lg border p-5 ${bq.status === "selected" ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`buyer-quote-${bq.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold text-[#0A2540]">{bq.label}</div>
                  <StatusBadge status={bq.status} />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="eyebrow text-slate-400">Unit price</div>
                    <div className="font-mono-num font-semibold text-[#0A2540]" data-testid={`bq-unit-${bq.id}`}>
                      {money(bq.buyer_unit_price ?? bq.buyer_price, bq.currency)} <span className="text-slate-500 text-xs font-normal">{per}</span>
                    </div>
                  </div>
                  <div>
                    <div className="eyebrow text-slate-400">Quantity</div>
                    <div className="font-medium text-[#0A2540]" data-testid={`bq-qty-${bq.id}`}>{qtyLabel(bq.quantity ?? rfq.quantity, bq.uom || uom)}</div>
                  </div>
                </div>

                <div className="mt-4 p-4 rounded-md bg-[#0A2540]/[0.03] border border-[#0A2540]/10">
                  <div className="eyebrow text-slate-500">Total landed price</div>
                  <div className="font-mono-num text-3xl font-extrabold text-[#0A2540] mt-1" data-testid={`bq-total-${bq.id}`}>
                    {money(bq.buyer_total_price ?? bq.buyer_price, bq.currency)}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Lead time</span><span className="font-medium">{bq.lead_time_days} days</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Warranty</span><span className="font-medium">{bq.warranty || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="font-medium text-right">{bq.shipping_terms || "—"}</span></div>
                </div>
                {bq.notes && <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-3">{bq.notes}</div>}
                {rfq.status === "Comparison" && bq.status !== "selected" && bq.status !== "declined" && (
                  <div className="flex gap-2 mt-5">
                    <Button size="sm" className="bg-[#0A2540] hover:bg-[#061A2E] gap-1 flex-1" onClick={() => selectBQ(bq.id)} data-testid={`select-quote-${bq.id}`}><Check className="w-4 h-4" /> Select</Button>
                    <Button size="sm" variant="outline" onClick={() => declineBQ(bq.id)} data-testid={`decline-quote-${bq.id}`}><X className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Supplier submit form / locked read-only view */}
      {user.role === "supplier" && (() => {
        const locked = rfq.quotes_locked || ["Awarded", "Delivered", "Closed"].includes(rfq.status);
        if (locked && rfq.my_quote) {
          const sq = rfq.my_quote;
          const outcome = sq.status === "awarded" ? "Awarded"
            : sq.status === "not_selected" ? "Not selected"
            : rfq.status === "Awarded" ? "Not selected"
            : "Locked";
          const outcomeColor = outcome === "Awarded"
            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
            : outcome === "Not selected"
            ? "bg-rose-50 text-rose-800 border-rose-200"
            : "bg-slate-100 text-slate-700 border-slate-300";
          return (
            <Card className="p-6 border-slate-200" data-testid="supplier-quote-locked">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                <div>
                  <div className="eyebrow">Your quotation</div>
                  <h2 className="font-display text-xl font-bold text-[#0A2540] mt-0.5">Read-only — quotation locked</h2>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${outcomeColor}`} data-testid="supplier-outcome-badge">
                  {outcome}
                </span>
              </div>
              <div className="text-sm text-slate-500 mb-4">This RFQ has been awarded. Your submitted quotation is preserved below in read-only form and can no longer be edited.</div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-50 p-3 rounded-md">
                  <div className="eyebrow text-slate-500">Unit price</div>
                  <div className="font-mono-num font-semibold text-[#0A2540]" data-testid="supplier-locked-unit">{money(sq.unit_price ?? sq.base_price, sq.currency)} <span className="text-xs font-normal text-slate-500">{per}</span></div>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <div className="eyebrow text-slate-500">Quantity requested</div>
                  <div className="font-medium text-[#0A2540]">{qtyLabel(rfq.quantity, uom)}</div>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <div className="eyebrow text-slate-500">Merchandise subtotal</div>
                  <div className="font-mono-num" data-testid="supplier-locked-subtotal">{money(sq.merchandise_subtotal ?? (Number(sq.unit_price ?? sq.base_price) * Number(rfq.quantity)), sq.currency)}</div>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <div className="eyebrow text-slate-500">Shipping / Other</div>
                  <div className="font-mono-num">{money(sq.shipping_cost ?? 0, sq.currency)} / {money(sq.other_costs ?? 0, sq.currency)}</div>
                </div>
                <div className="bg-slate-50 p-3 rounded-md md:col-span-2">
                  <div className="eyebrow text-slate-500">Supplier quote total</div>
                  <div className="font-mono-num text-xl font-extrabold text-[#0A2540]" data-testid="supplier-locked-total">{money(sq.supplier_total_cost ?? sq.merchandise_subtotal, sq.currency)}</div>
                </div>
                <div className="text-slate-500">Lead time</div><div className="font-medium">{sq.lead_time_days} days</div>
                <div className="text-slate-500">Warranty</div><div className="font-medium">{sq.warranty || "—"}</div>
                <div className="text-slate-500">Shipping terms</div><div className="font-medium">{sq.shipping_terms || "—"}</div>
                {sq.notes && (<><div className="text-slate-500">Notes</div><div className="text-slate-700 whitespace-pre-wrap">{sq.notes}</div></>)}
              </div>
              {sq.documents?.length > 0 && (
                <div className="mt-5">
                  <div className="eyebrow mb-2">Your attachments</div>
                  <div className="flex flex-wrap gap-2">
                    {sq.documents.map((d) => (
                      <a key={d.id} href="#" onClick={(e) => { e.preventDefault(); downloadFile(d.id, d.original_filename).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cannot open file")); }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 text-sm hover:bg-slate-50" data-testid={`sq-locked-doc-${d.id}`}>
                        <FileText className="w-4 h-4" /> {d.original_filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4 text-xs text-slate-500">Competitor quotations and buyer-facing commercial terms remain confidential.</div>
            </Card>
          );
        }
        return (
        <Card className="p-6 border-slate-200">
          <div className="eyebrow">Your quotation</div>
          <h2 className="font-display text-xl font-bold text-[#0A2540] mb-1">{rfq.my_quote ? "Update your quote" : "Submit quote"}</h2>
          <div className="text-sm text-slate-500 mb-4">Buyer is requesting <span className="font-medium text-[#0A2540]">{qtyLabel(rfq.quantity, uom)}</span>.</div>
          <form onSubmit={submitSupplierQuote} className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Price {per} (CAD) *</Label>
              <Input type="number" step="0.01" min="0" value={supQuote.unit_price} onChange={(e) => setSupQuote({ ...supQuote, unit_price: e.target.value })} required data-testid="sq-price-input" />
            </div>
            <div>
              <Label>Lead time (days) *</Label>
              <Input type="number" value={supQuote.lead_time_days} onChange={(e) => setSupQuote({ ...supQuote, lead_time_days: e.target.value })} required data-testid="sq-lead-input" />
            </div>
            <div>
              <Label>Shipping / delivery cost (CAD)</Label>
              <Input type="number" step="0.01" min="0" value={supQuote.shipping_cost} onChange={(e) => setSupQuote({ ...supQuote, shipping_cost: e.target.value })} data-testid="sq-shipping-cost-input" />
            </div>
            <div>
              <Label>Other costs (CAD)</Label>
              <Input type="number" step="0.01" min="0" value={supQuote.other_costs} onChange={(e) => setSupQuote({ ...supQuote, other_costs: e.target.value })} data-testid="sq-other-costs-input" />
            </div>
            <div><Label>Warranty</Label><Input value={supQuote.warranty} onChange={(e) => setSupQuote({ ...supQuote, warranty: e.target.value })} data-testid="sq-warranty-input" /></div>
            <div><Label>Shipping terms</Label><Input value={supQuote.shipping_terms} onChange={(e) => setSupQuote({ ...supQuote, shipping_terms: e.target.value })} data-testid="sq-shipping-input" /></div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={supQuote.notes} onChange={(e) => setSupQuote({ ...supQuote, notes: e.target.value })} data-testid="sq-notes-input" /></div>

            <div className="md:col-span-2">
              <Label>Quote attachments <span className="text-slate-400 text-xs font-normal">— PDF · DOCX · XLSX · PNG · JPG, up to {MAX_MB} MB each · max {MAX_QUOTE_FILES}</span></Label>
              <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-4">
                <label className={`flex items-center gap-2 text-sm text-slate-600 ${supDocs.length >= MAX_QUOTE_FILES ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`} data-testid="sq-upload-label">
                  <Upload className="w-4 h-4" /> {supDocs.length >= MAX_QUOTE_FILES ? `Maximum ${MAX_QUOTE_FILES} files reached` : "Attach quote PDFs / supporting docs"}
                  <input type="file" className="hidden" multiple disabled={supDocs.length >= MAX_QUOTE_FILES} accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg" onChange={uploadSupDoc} data-testid="sq-file-input" />
                </label>
                {supDocs.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {supDocs.map((d) => (
                      <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 px-3 py-2 rounded">
                        <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" /> {d.filename || d.original_filename} <span className="text-xs text-slate-400">· {humanSize(d.size)}</span></span>
                        <button type="button" onClick={() => removeSupDoc(d.id)} className="text-slate-500 hover:text-rose-600" data-testid={`remove-sq-doc-${d.id}`}><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-xs text-slate-500 mt-2">Only Caprocure sees these files — competing suppliers cannot.</div>
              </div>
            </div>

            {supPreview && (
              <div className="md:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
                <div className="eyebrow text-slate-500 mb-2">Your quote preview</div>
                <div className="grid grid-cols-2 gap-y-1">
                  <div className="text-slate-500">Unit price</div>
                  <div className="font-mono-num text-right" data-testid="sq-preview-unit">{money(supPreview.unit_price)} {per}</div>
                  <div className="text-slate-500">Merchandise subtotal ({qtyLabel(rfq.quantity, uom)} × {money(supPreview.unit_price)})</div>
                  <div className="font-mono-num text-right" data-testid="sq-preview-subtotal">{money(supPreview.subtotal)}</div>
                  <div className="text-slate-500">Shipping / delivery cost</div>
                  <div className="font-mono-num text-right" data-testid="sq-preview-shipping">{money(supPreview.shipping)}</div>
                  <div className="text-slate-500">Other costs</div>
                  <div className="font-mono-num text-right" data-testid="sq-preview-other">{money(supPreview.other)}</div>
                  <div className="col-span-2 border-t border-slate-200 mt-2 pt-2 flex justify-between items-center">
                    <span className="font-semibold text-[#0A2540]">Supplier quote total</span>
                    <span className="font-mono-num font-extrabold text-[#0A2540] text-lg" data-testid="sq-preview-total">{money(supPreview.total)}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#061A2E]" data-testid="sq-submit-btn">{rfq.my_quote ? "Update quote" : "Submit quote"}</Button>
            </div>
          </form>
          <div className="mt-3 text-xs text-slate-500">Your quote is confidential — competitors cannot see it. Caprocure will present your offer to the buyer in a neutral comparison format.</div>
        </Card>
        );
      })()}

      {/* Employee tabs — assembled from permissions the caller actually holds. */}
      {hasAnyEmployeeTab && (
        <Tabs defaultValue={canReadSupplierQuotes ? "quotes" : (canInvite ? "invites" : (canBuildBQ ? "buyer" : "supplier-doc"))} className="w-full">
          <TabsList data-testid="admin-tabs">
            {canReadSupplierQuotes && <TabsTrigger value="quotes" data-testid="tab-quotes">Supplier Quotes ({rfq.supplier_quotes?.length || 0})</TabsTrigger>}
            {canInvite && <TabsTrigger value="invites" data-testid="tab-invites">Invitations ({rfq.invitations?.length || 0})</TabsTrigger>}
            {canBuildBQ && <TabsTrigger value="buyer" data-testid="tab-buyer">Buyer Package ({rfq.buyer_quotes?.length || 0})</TabsTrigger>}
            <TabsTrigger value="supplier-doc" data-testid="tab-supplier-doc">Supplier Document</TabsTrigger>
          </TabsList>

          {canReadSupplierQuotes && (
          <TabsContent value="quotes">
            <Card className="p-6 border-slate-200">
              {rfq.supplier_quotes?.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No supplier quotes submitted yet.</div>
              ) : (
                <div className="space-y-3">
                  {rfq.supplier_quotes.map((sq) => (
                    <div key={sq.id} className="border border-slate-200 rounded-lg p-4 flex flex-wrap gap-4 items-center justify-between" data-testid={`supplier-quote-${sq.id}`}>
                      <div>
                        <div className="font-medium text-[#0A2540]">{sq.supplier?.company_name}</div>
                        <div className="text-xs text-slate-500">{sq.supplier?.name} · Submitted {new Date(sq.submitted_at).toLocaleDateString()}</div>
                        {sq.documents?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {sq.documents.map((d) => (
                              <a key={d.id} href="#" onClick={(e) => { e.preventDefault(); downloadFile(d.id, d.original_filename).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cannot open file")); }}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-200 text-xs text-slate-600 hover:bg-slate-50" data-testid={`admin-sq-doc-${d.id}`}>
                                <FileText className="w-3 h-3" /> {d.original_filename}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-6 items-center flex-wrap">
                        <div><div className="eyebrow text-slate-400">Unit</div><div className="font-mono-num font-bold text-[#0A2540]" data-testid={`sq-unit-${sq.id}`}>{money(sq.unit_price ?? sq.base_price, sq.currency)} <span className="text-xs font-normal text-slate-500">{per}</span></div></div>
                        <div><div className="eyebrow text-slate-400">Merch subtotal</div><div className="font-mono-num text-sm" data-testid={`sq-subtotal-${sq.id}`}>{money(sq.merchandise_subtotal ?? (Number(sq.unit_price ?? sq.base_price) * Number(rfq.quantity)), sq.currency)}</div></div>
                        <div><div className="eyebrow text-slate-400">Ship</div><div className="font-mono-num text-sm">{money(sq.shipping_cost ?? 0, sq.currency)}</div></div>
                        <div><div className="eyebrow text-slate-400">Other</div><div className="font-mono-num text-sm">{money(sq.other_costs ?? 0, sq.currency)}</div></div>
                        <div><div className="eyebrow text-slate-400">Supplier total</div><div className="font-mono-num font-bold text-[#0A2540]" data-testid={`sq-total-${sq.id}`}>{money(sq.supplier_total_cost ?? sq.merchandise_subtotal, sq.currency)}</div></div>
                        <div><div className="eyebrow text-slate-400">Lead</div><div className="text-sm">{sq.lead_time_days}d</div></div>
                        <Button size="sm" onClick={() => { setOpenBQ(sq); setBqForm({ markup_pct: 12, lead_time_days: sq.lead_time_days, warranty: sq.warranty, shipping_terms: sq.shipping_terms, notes: "" }); }}
                          disabled={!canBuildBQ}
                          className="bg-[#0A2540] hover:bg-[#061A2E] disabled:opacity-40" data-testid={`build-bq-${sq.id}`}>Build buyer quote</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
          )}

          {canInvite && (
          <TabsContent value="invites">
            <Card className="p-6 border-slate-200">
              <div className="mb-4">
                <div className="eyebrow">Invite suppliers</div>
                <h3 className="font-display font-bold text-[#0A2540]">Match & invite for {rfq.category}</h3>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {suppliers.map((s) => {
                  const alreadyInvited = rfq.invitations?.some((i) => i.supplier_id === s.id);
                  return (
                    <label key={s.id} className={`flex items-center justify-between p-3 rounded-md border ${alreadyInvited ? "bg-slate-50 border-slate-200 opacity-70" : "border-slate-200 hover:bg-slate-50"} cursor-pointer`}>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" disabled={alreadyInvited} checked={!!invSelect[s.id]} onChange={(e) => setInvSelect({ ...invSelect, [s.id]: e.target.checked })} data-testid={`invite-cb-${s.id}`} />
                        <div>
                          <div className="text-sm font-medium text-[#0A2540]">{s.company_name}</div>
                          <div className="text-xs text-slate-500">{s.categories?.join(", ")}</div>
                        </div>
                      </div>
                      {alreadyInvited && <span className="text-xs text-slate-500">Already invited</span>}
                    </label>
                  );
                })}
                {suppliers.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No suppliers in this category.</div>}
              </div>
              <div className="mt-4 flex flex-col gap-2">
                {rfq.supplier_package?.status !== "approved" && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="invite-blocked-notice">
                    Approve the supplier attachment package above before you can send invitations.
                  </div>
                )}
                <div className="flex justify-end">
                  <Button onClick={invite}
                    disabled={!Object.values(invSelect).some(Boolean) || rfq.supplier_package?.status !== "approved"}
                    className="bg-[#0A2540] hover:bg-[#061A2E]"
                    data-testid="invite-btn">Invite selected</Button>
                </div>
              </div>

              {rfq.invitations?.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="eyebrow mb-2">Already invited</div>
                  <div className="space-y-2">
                    {rfq.invitations.map((i) => (
                      <div key={i.id} className="flex justify-between text-sm py-2 border-b border-slate-100">
                        <span>{i.supplier?.company_name}</span>
                        <StatusBadge status={i.status} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>
          )}

          {canBuildBQ && (
          <TabsContent value="buyer">
            <Card className="p-6 border-slate-200">
              {rfq.buyer_quotes?.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Build buyer-facing quotes from the Supplier Quotes tab.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-3">Option</th>
                        <th className="px-4 py-3">Qty × UOM</th>
                        {canReadSupplierCost && <th className="px-4 py-3">Sup. unit</th>}
                        {canReadSupplierCost && <th className="px-4 py-3">Sup. total</th>}
                        {canReadMargin && <th className="px-4 py-3">Markup %</th>}
                        <th className="px-4 py-3">Buyer unit</th>
                        <th className="px-4 py-3">Buyer total</th>
                        {canReadMargin && <th className="px-4 py-3 text-[#C8102E]">CA Revenue</th>}
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rfq.buyer_quotes.map((bq) => (
                        <tr key={bq.id} data-testid={`bq-row-${bq.id}`}>
                          <td className="px-4 py-3 font-medium text-[#0A2540]">{bq.label}</td>
                          <td className="px-4 py-3">{qtyLabel(bq.quantity ?? rfq.quantity, bq.uom || uom)}</td>
                          {canReadSupplierCost && <td className="px-4 py-3 font-mono-num">{money(bq.supplier_unit_price, bq.currency)}</td>}
                          {canReadSupplierCost && <td className="px-4 py-3 font-mono-num">{money(bq.supplier_total_cost, bq.currency)}</td>}
                          {canReadMargin && <td className="px-4 py-3 font-mono-num">{bq.markup_pct != null ? `${bq.markup_pct}%` : "—"}</td>}
                          <td className="px-4 py-3 font-mono-num">{money(bq.buyer_unit_price, bq.currency)}</td>
                          <td className="px-4 py-3 font-mono-num font-bold">{money(bq.buyer_total_price, bq.currency)}</td>
                          {canReadMargin && <td className="px-4 py-3 font-mono-num text-[#C8102E] font-bold">{money(bq.margin_amount, bq.currency)}</td>}
                          <td className="px-4 py-3"><StatusBadge status={bq.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
          )}

          <TabsContent value="supplier-doc">
            <SupplierDocumentTab rfq={rfq} onRefresh={load} />
          </TabsContent>
        </Tabs>
      )}

      {/* Margin builder modal — responsive: header + footer stay pinned, body scrolls */}
      <Dialog open={!!openBQ} onOpenChange={(o) => !o && setOpenBQ(null)}>
        <DialogContent
          className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] sm:max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden"
          data-testid="bq-modal-content"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0">
            <DialogTitle>Build buyer-facing quote</DialogTitle>
          </DialogHeader>
          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4"
            data-testid="bq-modal-scroll"
          >
          {openBQ && bqPreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 p-3 rounded-md">
                  <div className="text-xs text-slate-500">Quantity × UOM</div>
                  <div className="font-medium text-[#0A2540]">{qtyLabel(bqPreview.qty, uom)}</div>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <div className="text-xs text-slate-500">Supplier unit price</div>
                  <div className="font-mono-num text-xl font-bold text-[#0A2540]" data-testid="bq-modal-sup-unit">{money(bqPreview.supUnit)} <span className="text-xs font-normal text-slate-500">{per}</span></div>
                </div>
              </div>

              <div className="rounded-md border border-slate-200 p-4">
                <div className="eyebrow text-slate-500 mb-2">Supplier cost breakdown</div>
                <div className="grid grid-cols-2 gap-y-1 text-sm">
                  <div className="text-slate-500">Merchandise subtotal</div>
                  <div className="font-mono-num text-right" data-testid="bq-modal-sup-merch">{money(bqPreview.supMerch)}</div>
                  <div className="text-slate-500">Shipping</div>
                  <div className="font-mono-num text-right">{money(bqPreview.ship)}</div>
                  <div className="text-slate-500">Other costs</div>
                  <div className="font-mono-num text-right">{money(bqPreview.other)}</div>
                  <div className="col-span-2 border-t border-slate-200 mt-2 pt-2 flex justify-between">
                    <span className="font-semibold">Supplier total cost</span>
                    <span className="font-mono-num font-bold" data-testid="bq-modal-sup-total">{money(bqPreview.supTotal)}</span>
                  </div>
                </div>
              </div>

              <div>
                <Label>Markup % (applied to supplier unit price)</Label>
                <Input type="number" step="0.01" value={bqForm.markup_pct} onChange={(e) => setBqForm({ ...bqForm, markup_pct: e.target.value })} data-testid="bq-markup-input" disabled={!canSetMarkup && toNum(bqForm.markup_pct) !== 0} />
                {!canSetMarkup && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="bq-markup-locked">Your role cannot set a non-zero markup — only pass-through (0%) buyer quotes will be accepted.</p>
                )}
              </div>

              <div className="rounded-md border-2 border-[#0A2540]/20 bg-[#0A2540]/[0.03] p-4">
                <div className="eyebrow text-[#0A2540] mb-2">Buyer-facing calculation</div>
                <div className="grid grid-cols-2 gap-y-1 text-sm">
                  <div className="text-slate-500">Buyer unit price</div>
                  <div className="font-mono-num text-right font-semibold" data-testid="bq-modal-buyer-unit">{money(bqPreview.buyerUnit)} <span className="text-xs font-normal text-slate-500">{per}</span></div>
                  <div className="text-slate-500">Buyer merchandise total ({qtyLabel(bqPreview.qty, uom)} × {money(bqPreview.buyerUnit)})</div>
                  <div className="font-mono-num text-right">{money(bqPreview.buyerMerch)}</div>
                  <div className="text-slate-500">+ Shipping & other</div>
                  <div className="font-mono-num text-right">{money(bqPreview.ship + bqPreview.other)}</div>
                  <div className="col-span-2 border-t border-slate-300 mt-2 pt-2 flex justify-between items-center">
                    <span className="font-bold text-[#0A2540]">Buyer total landed price</span>
                    <span className="font-mono-num font-extrabold text-[#0A2540] text-lg" data-testid="bq-modal-buyer-total">{money(bqPreview.buyerTotal)}</span>
                  </div>
                  {canReadMargin && (
                    <div className="col-span-2 flex justify-between items-center pt-1">
                      <span className="font-semibold text-[#C8102E]">Caprocure revenue</span>
                      <span className="font-mono-num font-bold text-[#C8102E]" data-testid="bq-modal-margin">{money(bqPreview.margin)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Lead time (days)</Label><Input type="number" value={bqForm.lead_time_days} onChange={(e) => setBqForm({ ...bqForm, lead_time_days: e.target.value })} data-testid="bq-lead-input" /></div>
                <div><Label>Warranty</Label><Input value={bqForm.warranty} onChange={(e) => setBqForm({ ...bqForm, warranty: e.target.value })} data-testid="bq-warranty-input" /></div>
              </div>
              <div><Label>Shipping terms (visible to buyer)</Label><Input value={bqForm.shipping_terms} onChange={(e) => setBqForm({ ...bqForm, shipping_terms: e.target.value })} data-testid="bq-shipping-input" /></div>
              <div><Label>Notes (visible to buyer)</Label><Textarea rows={2} value={bqForm.notes} onChange={(e) => setBqForm({ ...bqForm, notes: e.target.value })} data-testid="bq-notes-input" /></div>
            </div>
          )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-200 shrink-0 bg-white">
            <Button variant="outline" onClick={() => setOpenBQ(null)}>Cancel</Button>
            <Button onClick={submitBuyerQuote} className="bg-[#0A2540] hover:bg-[#061A2E]" data-testid="bq-submit-btn">Release to buyer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
